import { useEffect, useCallback, useState } from 'react';

// Third-party
import moment from 'moment-timezone';
import { Moment } from 'moment/moment';

// Redux-toolkit
import { getTimezone } from '@mgm/common/utils';
import {
  getScheduleList,
  getSelectedLocationId,
  getSelectedScheduleShift,
  getSelectedShiftDate,
  getSelectedShiftMember,
} from '../../../../store/schedules/selectors';
import { getMembers } from '../../../../store/team/selectors';

// Hooks
import { useAppSelector } from '../../../../hooks/useAppStore';
import { useCustomForm } from '../../../../hooks/useForm';

// Data
import { INITIAL_SCHEDULE_SHIFT } from '../../../../data';

// Types
import { TMember, TScheduleShift } from '../../../../types';

// Utils
import { dateFormat } from '../../../../utils/helpers';

export function useEditScheduleShift() {
  const selectedShift = useAppSelector(getSelectedScheduleShift) as TScheduleShift;
  const selectedShiftDate = useAppSelector(getSelectedShiftDate);
  const selectedShiftMember = useAppSelector(getSelectedShiftMember);
  const members = useAppSelector(getMembers);
  const [mode, setMode] = useState('create');

  const {
    formInputs,
    setFormInputs,
    isEdited,
    setPrevFormInputs,
    handleFormChange,
  } = useCustomForm(INITIAL_SCHEDULE_SHIFT);

  useEffect(() => {
    if (selectedShift.id) {
      setMode('edit');

      const employee = members.find((member) => member.email === selectedShift.email);

      const shiftData = {
        location: selectedShift.location,
        id: selectedShift.id as number,
        employee: employee || {} as TMember,
        date: moment.tz(selectedShift.start, selectedShift.timezone).startOf('day'),
        startTime: moment.tz(selectedShift.start, selectedShift.timezone),
        endTime: moment.tz(selectedShift.end, selectedShift.timezone),
        breaks: selectedShift.breaks?.map((breakData) => ({
          ...breakData,
          breakStart: moment.tz(breakData.breakStart, selectedShift.timezone),
          breakEnd: moment.tz(breakData.breakEnd, selectedShift.timezone),
        })),
      };

      setFormInputs(shiftData as unknown as typeof INITIAL_SCHEDULE_SHIFT);
      setPrevFormInputs(shiftData as unknown as typeof INITIAL_SCHEDULE_SHIFT);
    } else {
      setMode('create');

      const shiftData = {
        ...INITIAL_SCHEDULE_SHIFT,
        date: selectedShiftDate ? moment.tz(selectedShiftDate, getTimezone()) : null,
        employee: selectedShiftMember || null,
      };
      setFormInputs(shiftData);
    }
  }, [selectedShift, selectedShiftDate, selectedShiftMember]);

  const emptyBulkShifts: typeof INITIAL_SCHEDULE_SHIFT[] = Array.from(
    { length: 5 },
    (_, index) => ({
      ...INITIAL_SCHEDULE_SHIFT,
      id: index + 1,
    }),
  );

  return {
    mode,
    formInputs,
    handleFormChange,
    isEdited,
    emptyBulkShifts,
    setFormInputs,
  };
}

export function useSchedules() {
  const scheduleList = useAppSelector(getScheduleList);
  const selectedLocationId = useAppSelector(getSelectedLocationId);

  const getPersonShifts = useCallback(
    (email: string, date: Moment) => {
      const shiftList = scheduleList[dateFormat(date, 'D/M/YYYY')]?.schedules || [];

      return shiftList?.filter(
        (shift) => shift.email === email && (selectedLocationId > 0
          ? shift.location.id === selectedLocationId : true),
      );
    },
    [scheduleList, selectedLocationId], // Dependencies
  );

  return {
    getPersonShifts,
  };
}
